import React, { lazy, Suspense, type ComponentProps } from 'react';

import Loadable from 'react-loadable';

import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { BLOCK_CONTROLS_RIGHT_GROUP } from '@atlaskit/editor-common/block-controls/surface-keys';
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { RegisterButton } from '@atlaskit/editor-ui-control-model/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';
import { EditorRemixLifecycleOwnerProvider } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';

import { makeDuplicateAndReplaceMauiApp } from './editor-actions/duplicate-and-replace-maui-app';
import { makeFinalizeMauiEmbedFromIndicator } from './editor-actions/finalize-maui-embed-from-indicator';
import { makeAddBackendRemixHistoryStackEntryCommand } from './editor-commands/addBackendRemixHistoryStackEntry';
import { clearRemixThinkingDecoration } from './editor-commands/clear-remix-thinking-decoration';
import { makeCloseRemixModal } from './editor-commands/close-remix-modal';
import { makeOpenRemixModal } from './editor-commands/open-remix-modal';
import { replaceMauiApp } from './editor-commands/replace-maui-app';
import { replaceWhiteboardEmbed } from './editor-commands/replace-whiteboard-embed';
import { setMauiEmbedFinalizeStatus } from './editor-commands/set-maui-embed-finalize-status';
import { setRemixImageMediaId } from './editor-commands/set-remix-image-media-id';
import { makeStartRemix } from './editor-commands/start-remix';
import { makeStartRemixThinking } from './editor-commands/start-remix-thinking';
import { stopRemixThinking } from './editor-commands/stop-remix-thinking';
import {
	dismissSubtypeAdjustedFlag,
	showSubtypeAdjustedFlag,
} from './editor-commands/subtype-adjusted-flag';
import {
	makeTrackVariantUpdateCompleteCommand,
	makeTrackVariantUpdateStartCommand,
} from './editor-commands/trackVariantUpdate';
import { makeUpdateRemixImageHistoryFieldIdCommand } from './editor-commands/updateRemixImageHistoryFieldId';
import type { MalleableUiPlugin, MalleableUiSharedState } from './malleableUiPluginType';
import { remixButtonDecoration } from './pm-plugins/decorations-remix-button';
import { createEditorViewCapturePlugin } from './pm-plugins/editor-view-capture-plugin';
import {
	createMauiDiagramPastePmPlugin,
	mauiDiagramPastePluginKey,
	type MauiDiagramPasteConfig,
} from './pm-plugins/maui-diagram-paste-plugin';
import {
	createMalleableUiPmPlugin,
	createToolbarSuppressionPlugin,
	malleableUiPluginKey,
} from './pm-plugins/plugin-key';
import { getHistoryEntryForUpdate } from './pm-plugins/remix-history/getHistoryEntryForUpdate';
import { createRemixHistoryPmPlugin } from './pm-plugins/remix-history/remix-history-plugin';
import { createRemixHoverHighlightPmPlugin } from './pm-plugins/remix-hover-highlight-plugin';
import {
	createRemixThinkingPmPlugin,
	remixThinkingPluginKey,
} from './pm-plugins/remix-thinking/remix-thinking-plugin';
import { getRemixBlockMenuComponents } from './ui/block-menu/remix-block-menu-item';
import { EditorInlineRovoEditLifecycleHost } from './ui/components/EditorInlineRovoEditLifecycleHost';
import { MauiDiagramPasteFlag } from './ui/components/MauiDiagramPasteFlag';
import type { MauiEmbedSpacePickerProps } from './ui/components/MauiEmbedSpacePicker';
import { PersistedSelectionHighlightStyles } from './ui/components/PersistedSelectionHighlightStyles';
import { RemixContent } from './ui/components/RemixContent';
import { RemixRightSurfaceButton } from './ui/components/RemixRightSurfaceButton';
import { SubtypeAdjustedFlag } from './ui/components/SubtypeAdjustedFlag';
import { EditorRemixEffectHost } from './ui/editorRemixEffectHost';
import { EditorRemixRecoveryCoordinatorProvider } from './ui/editorRemixRecoveryCoordinator';
import { getPasteMenuItems } from './ui/paste-menu/get-paste-menu-items';
import { shouldShowRemixForSelectionContext } from './ui/remix-config';
import { makeRemixFloatingToolbarButton } from './ui/toolbars/floating-toolbar/floating-toolbar-remix';
import { makeRemixVariantFloatingToolbarButton } from './ui/toolbars/floating-toolbar/floating-toolbar-remix-variant';
import { getOverriddenFloatingToolbarConfig } from './ui/toolbars/overridden-floating-toolbar-config';
import { getSelectionToolbarComponents } from './ui/toolbars/toolbar-components';
import {
	beginEditorInlineRovoEdit,
	claimEditorInlineRovoEdit,
	reportEditorInlineRovoEdit,
} from './ui/utils/editor-inline-rovo-edit-lifecycle';

const REMIX_BUTTON_DECORATION_TYPE = 'REMIX_BUTTON';

const MauiEmbedSpacePickerLazyComponent = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_editor-malleable-ui-maui-embed-space-picker" */ './ui/components/MauiEmbedSpacePicker'
	).then((mod) => ({ default: mod.MauiEmbedSpacePicker })),
);
Object.assign(MauiEmbedSpacePickerLazyComponent, { displayName: 'lazy(MauiEmbedSpacePicker)' });

const MauiEmbedSpacePickerLoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_editor-malleable-ui-maui-embed-space-picker" */ './ui/components/MauiEmbedSpacePicker'
		).then((mod) => mod.MauiEmbedSpacePicker) as Promise<
			React.ComponentType<React.PropsWithChildren<MauiEmbedSpacePickerProps>>
		>,
	loading: () => null,
});

function MauiEmbedSpacePickerWithSuspense(
	props: ComponentProps<typeof MauiEmbedSpacePickerLazyComponent>,
): React.JSX.Element {
	return (
		<Suspense fallback={null}>
			{React.createElement(MauiEmbedSpacePickerLazyComponent, props)}
		</Suspense>
	);
}

const LoadableMauiEmbedSpacePicker = (
	props: ComponentProps<typeof MauiEmbedSpacePickerLazyComponent> & React.RefAttributes<unknown>,
): React.JSX.Element =>
	fg('nike_r19_react_loadable')
		? React.createElement(MauiEmbedSpacePickerWithSuspense, props)
		: React.createElement(MauiEmbedSpacePickerLoadableComponent, props);

export const malleableUiPlugin: MalleableUiPlugin = ({ api, config }) => {
	const isRovoLLMEnabled = config?.__isRovoLLMEnabled ?? false;
	const isRemixEnabled =
		isRovoLLMEnabled && expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
	const isRightSideEnabled = isRemixEnabled && fg('confluence_remix_button_right_side_block_fg');
	const remixDisabledReason = config?.__remixDisabledReason;
	const isRemixDisabled = !!remixDisabledReason;
	const isRemixHidden = config?.__isRemixHidden;
	const isRemixAvailable = isRemixEnabled && !isRemixHidden && !isRemixDisabled;
	const remixContentCreatorResult =
		isRemixAvailable &&
		api !== undefined &&
		typeof config?.createRemixContentComponent === 'function'
			? config.createRemixContentComponent()
			: undefined;
	const remixContentComponent =
		typeof remixContentCreatorResult === 'function'
			? remixContentCreatorResult
			: remixContentCreatorResult?.Component;
	const editorRemixLifecycleAdapterHost =
		typeof remixContentCreatorResult === 'function'
			? undefined
			: remixContentCreatorResult?.LifecycleHost;
	const hasEditorRemixLifecycleAdapter =
		typeof remixContentCreatorResult !== 'function' &&
		remixContentCreatorResult?.capabilities.editorRemixLifecycleAdapter === true;
	// The editor owning surface is the single rollout boundary: unavailable and adapter-less hosts
	// (including hosts that cannot render Remix content) short-circuit before this regular evaluation
	// records control or treatment exposure.
	const isEditorRemixStateRearchitectureTreatment =
		isRemixAvailable &&
		hasEditorRemixLifecycleAdapter &&
		editorRemixLifecycleAdapterHost !== undefined &&
		remixContentComponent !== undefined &&
		api !== undefined &&
		fg('remix_state_rearchitecture_enabled') &&
		isExperimentEnabled('remix_state_rearchitecture_editor');
	const remixToolbarOptions = { remixDisabledReason, isDisabled: isRemixDisabled, isRemixHidden };

	// Mutable reference to the editor view, set by a PM plugin on init.
	// Used by the Remix block-menu isHidden callback to compute text length.
	let editorView: EditorView | undefined;

	if (api && !isRemixHidden && isRemixEnabled) {
		api.toolbar?.actions.registerComponents(
			getSelectionToolbarComponents(api, remixToolbarOptions),
		);

		// Register Remix in the block menu when the experiment is enabled and
		// Remix is not disabled (e.g. restricted-content contexts).
		// Use NoExposure here since this runs on every editor load — exposure is
		// recorded when the block menu item is actually rendered.
		if (
			!isRemixDisabled &&
			expValEqualsNoExposure('platform_editor_remix_in_block_menu', 'isEnabled', true)
		) {
			api.blockMenu?.actions.registerBlockMenuComponents(
				getRemixBlockMenuComponents({ api, getEditorView: () => editorView }),
			);
		}

		if (
			!isRemixDisabled &&
			['hasSpellingAndGrammar', 'hasAltAiActions'].includes(
				expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
			)
		) {
			api.uiControlRegistry?.actions.register(getPasteMenuItems({ api }));
		}
	}

	if (api?.blockControls && !config?.__isRemixHidden && isRightSideEnabled) {
		const livePage = config?.__livePage;
		// EDITOR-8771: prefer the right surface via the UI Control Registry API once block controls
		// are migrated, falling back to `registerNodeDecoration` on the legacy path.
		if (isExperimentEnabled('platform_editor_block_control_migration')) {
			const remixRightSurfaceButton: RegisterButton = {
				component: ({ surfaceContext }) => (
					<RemixRightSurfaceButton
						surfaceContext={surfaceContext}
						api={api}
						isDisabled={isRemixDisabled}
						isRovoLLMEnabled={isRovoLLMEnabled}
						livePage={livePage}
						remixDisabledReason={remixDisabledReason}
					/>
				),
				isHidden: ({ surfaceContext } = {}) => {
					const context = surfaceContext?.get(BLOCK_CONTROL_UI_CONTEXT);
					if (!context) {
						return true;
					}
					// Remix is a root-node-only control
					if (
						context.targetNode.pos !== context.rootNode.pos ||
						context.targetNode.pos !== (context.activeNode?.rootPos ?? context.activeNode?.pos)
					) {
						return true;
					}
					return !shouldShowRemixForSelectionContext({ node: context.rootNode.node });
				},
				key: 'remix-right-surface-button',
				parents: [{ ...BLOCK_CONTROLS_RIGHT_GROUP, rank: 100 }],
				type: 'button',
			};
			api.uiControlRegistry?.actions.register([remixRightSurfaceButton]);
		} else {
			api.blockControls.actions.registerNodeDecoration({
				type: REMIX_BUTTON_DECORATION_TYPE,
				create: (params) =>
					remixButtonDecoration({
						...params,
						api,
						livePage,
						remixDisabledReason,
						isDisabled: isRemixDisabled,
						isRovoLLMEnabled,
					}),
				showInViewMode: true,
				shouldCreate: (params) => {
					const { editorState, rootPos } = params;
					if (rootPos === undefined) {
						return false;
					}
					const node = editorState.doc.nodeAt(rootPos);
					return shouldShowRemixForSelectionContext({ node: node ?? undefined });
				},
			});
		}
	}

	return {
		name: 'malleableUi',

		pmPlugins() {
			const plugins = [
				{
					name: 'malleableUi',
					plugin: ({ dispatch }: { dispatch: Dispatch }) => createMalleableUiPmPlugin(dispatch),
				},
				{
					name: 'malleableUiEditorViewCapture',
					plugin: () =>
						createEditorViewCapturePlugin(
							(view) => {
								editorView = view;
							},
							() => {
								editorView = undefined;
							},
						),
				},
				{
					name: 'malleableUiToolbarSuppression',
					plugin: () => createToolbarSuppressionPlugin(),
				},
				{
					name: 'remixThinking',
					plugin: ({ dispatch, getIntl, nodeViewPortalProviderAPI }: PMPluginFactoryParams) =>
						createRemixThinkingPmPlugin({
							api,
							config,
							dispatch,
							getIntl,
							nodeViewPortalProviderAPI,
						}),
				},
				...(fg('cc-maui-phase-3')
					? [
							{
								name: 'remixHoverHighlight',
								plugin: () => createRemixHoverHighlightPmPlugin(),
							},
						]
					: []),
				...(fg('platform_editor_ai_remix_toolbar')
					? [
							{
								name: 'remixHistory',
								plugin: ({ dispatch }: PMPluginFactoryParams) =>
									createRemixHistoryPmPlugin({ api, dispatch }),
							},
						]
					: []),
			];

			if (config?.diagramPaste) {
				const diagramPasteConfig: MauiDiagramPasteConfig = {
					...config.diagramPaste,
					fireAnalyticsEvent: (payload) => {
						api?.analytics?.actions.fireAnalyticsEvent(payload as AnalyticsEventPayload);
					},
				};
				plugins.push({
					name: 'mauiDiagramPaste',
					plugin: ({ dispatch, getIntl }: PMPluginFactoryParams) =>
						createMauiDiagramPastePmPlugin(dispatch, diagramPasteConfig, getIntl),
				});
			}

			return plugins;
		},

		getSharedState(editorState): MalleableUiSharedState | undefined {
			if (!editorState) {
				return undefined;
			}
			const malleableUiState = malleableUiPluginKey.getState(editorState);
			const remixThinkingState = remixThinkingPluginKey.getState(editorState);
			if (!malleableUiState || !remixThinkingState) {
				return undefined;
			}
			return {
				aiEditReplacementLocalIds: malleableUiState.aiEditReplacementLocalIds,
				inFlightVariantUpdates: malleableUiState.inFlightVariantUpdates,
				isUpdate: remixThinkingState.isUpdate ?? false,
				historyEntryForUpdate: remixThinkingState.historyEntryForUpdate,
				dynamicUiSubtypeForUpdate: remixThinkingState.dynamicUiSubtypeForUpdate,
				isComplete: remixThinkingState.isComplete ?? false,
				isAnyRemixing: malleableUiState.isAnyRemixing,
				isRemixModalOpen: malleableUiState.isRemixModalOpen,
				remixModalMetadata: malleableUiState.remixModalMetadata,
				remixAttemptId: remixThinkingState.remixAttemptId,
				isThinking: remixThinkingState.isThinking,
				remixSource: malleableUiState.remixSource,
				remixDiagramContentId: malleableUiState.remixDiagramContentId,
				remixUpdateScrollTargetUrl: malleableUiState.remixUpdateScrollTargetUrl,
				remixImageMediaId: malleableUiState.remixImageMediaId,
				showLocationPicker: malleableUiState.showLocationPicker,
				showSubtypeAdjustedFlag: malleableUiState.showSubtypeAdjustedFlag,
				diagramPaste: mauiDiagramPastePluginKey.getState(editorState),
				savedSelection: remixThinkingState.savedSelection,
			};
		},

		actions: {
			beginInlineRovoEdit: (targetId) => beginEditorInlineRovoEdit(api, targetId),
			claimInlineRovoEdit: (targetId) => claimEditorInlineRovoEdit(api, targetId),
			duplicateAndReplaceMauiApp: makeDuplicateAndReplaceMauiApp({ api, config }),
			finalizeMauiEmbedFromIndicator: makeFinalizeMauiEmbedFromIndicator({ config }),
			getRemixDynamicUiSubtype: (fieldId: string): string | undefined =>
				editorView
					? getHistoryEntryForUpdate({ state: editorView.state, fieldId })?.dynamicUiSubtype
					: undefined,
			getRemixFloatingToolbarButton: makeRemixFloatingToolbarButton({
				api,
				options: remixToolbarOptions,
			}),
			getRemixVariantFloatingToolbarButton: makeRemixVariantFloatingToolbarButton({ api }),
			isVariantUpdateInFlight: (id: string): boolean => {
				const sharedState = api?.malleableUi?.sharedState?.currentState();
				return sharedState?.inFlightVariantUpdates.has(id) ?? false;
			},
			reportInlineRovoEdit: (attemptId, result) =>
				reportEditorInlineRovoEdit(api, attemptId, result),
			shouldDisableRemix: () => {
				const malleableUiState = api?.malleableUi?.sharedState.currentState();
				return Boolean(
					malleableUiState?.isRemixModalOpen ||
					malleableUiState?.isThinking ||
					malleableUiState?.isAnyRemixing ||
					api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled,
				);
			},
		},

		commands: {
			clearRemixThinkingDecoration,
			setMauiEmbedFinalizeStatus,
			setRemixImageMediaId,
			addBackendRemixHistoryStackEntry: makeAddBackendRemixHistoryStackEntryCommand,
			openRemixModal: makeOpenRemixModal(api),
			closeRemixModal: makeCloseRemixModal(api),
			startRemixThinking: makeStartRemixThinking(api),
			// Modal-free remix: anchors to pre-supplied localIds and starts remixing without opening
			// the Remix modal (used by Suggested Remix).
			startRemix: makeStartRemix(api),
			stopRemixThinking,
			replaceMauiApp,
			replaceWhiteboardEmbed,
			trackVariantUpdateStart: makeTrackVariantUpdateStartCommand,
			trackVariantUpdateComplete: makeTrackVariantUpdateCompleteCommand,
			updateRemixImageHistoryFieldId: makeUpdateRemixImageHistoryFieldIdCommand,
			showSubtypeAdjustedFlag,
			dismissSubtypeAdjustedFlag,
		},

		contentComponent: () => {
			return (
				<EditorRemixLifecycleOwnerProvider>
					<EditorRemixRecoveryCoordinatorProvider>
						<PersistedSelectionHighlightStyles />
						{/* Sibling of RemixContent, not a descendant: it must outlive the terminal cleanup
						    that unmounts the content component's subtree. */}
						{isEditorRemixStateRearchitectureTreatment ? <EditorRemixEffectHost api={api} /> : null}
						{isEditorRemixStateRearchitectureTreatment && editorRemixLifecycleAdapterHost ? (
							<EditorInlineRovoEditLifecycleHost
								api={api}
								LifecycleAdapterHost={editorRemixLifecycleAdapterHost}
							/>
						) : null}
						<RemixContent
							api={api}
							hiddenOptions={config?.hiddenOptions}
							remixContentComponent={remixContentComponent}
							isEditorRemixStateRearchitectureTreatment={isEditorRemixStateRearchitectureTreatment}
						/>
						<SubtypeAdjustedFlag api={api} productFlags={config?.productFlags} />
						{config?.diagramPaste && (
							<MauiDiagramPasteFlag api={api} productFlags={config?.productFlags} />
						)}
						{config?.diagramPaste &&
						// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
						!FeatureGates.getExperimentValue<boolean>(
							'maui-native-embed-finalize-diagram-anywhere',
							'value',
							false,
						) ? (
							<LoadableMauiEmbedSpacePicker api={api} config={config} />
						) : null}
					</EditorRemixRecoveryCoordinatorProvider>
				</EditorRemixLifecycleOwnerProvider>
			);
		},

		pluginsOptions:
			!config?.__isRemixHidden && !config?.__remixDisabledReason && isRemixEnabled
				? {
						floatingToolbar: getOverriddenFloatingToolbarConfig(api, {
							shouldHideToolbarWhileRemixing: config?.shouldHideToolbarWhileRemixing ?? false,
						}),
					}
				: {},
	};
};
